import { Injectable } from '@angular/core';
import { HttpClient,  HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, ReplaySubject, Subject, of, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay, tap} from 'rxjs/operators';
import { Title, Meta, MetaDefinition } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { active_session_details } from './users.service';
import { CookieService } from 'ngx-cookie-service';


@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  error_message: any;

  constructor(private http: HttpClient, private title: Title, private meta: Meta, private cookieService:CookieService) { }

  options = { headers: new HttpHeaders ({ 'Content-Type': 'application/json' }) }

  active_session = `${this.cookieService.get('user_session_id')}&bg_id=${this.cookieService.get('bg_id')}`

  // in-flight/resolved cms_keys, shared so repeated callers hit the API once per session
  private cmsKeys$: Observable<any>;

  // cms_keys may live at data.cms_keys or data.params_hash2.config_params.cms_keys
  // depending on the config version, so check both.
  private extractCmsKeys(res: any){
    const data = (res && res.data) ? res.data : {};
    return data.cms_keys
      || (data.params_hash2 && data.params_hash2.config_params && data.params_hash2.config_params.cms_keys)
      || null;
  }

  private cacheCmsKeys(cmsKeys: any){
    if (!cmsKeys) { return; }
    Object.keys(cmsKeys).forEach((key) => {
      if (cmsKeys[key]) {
        this.cookieService.set(key, cmsKeys[key], undefined, '/');
      }
    });
  }

  private cmsToken(): string {
    return this.cookieService.get('bg_authtoken') || environment.authtoken;
  }

  // Fetch app-config-params and cache each cms_keys value in a cookie, so the whole app
  // resolves catalog/entity IDs dynamically from the API instead of hardcoded literals.
  // Returns the Observable so callers can wait for it before navigating if they need to.
  loadCmsKeys(cms_token: string): Observable<any> {
    const req = this.http.get<any>(`${environment.apiURL}catalogs/message/items/app-config-params.gzip?auth_token=${cms_token}&region=IN&item_language=en`);
    req.subscribe(
      (res) => {
        const cmsKeys = this.extractCmsKeys(res);
        this.cacheCmsKeys(cmsKeys);
        // prime the shared cache so a later getCmsKeys() does not refetch
        if (cmsKeys) { this.cmsKeys$ = of(cmsKeys); }
      },
      () => { /* ignore: nothing cached, getters return '' until a successful load */ }
    );
    return req;
  }

  // cms_keys straight from the API. The cookies written at login are only a warm start —
  // this keeps working when they are missing (fresh browser, no login yet), which is why
  // the transcoder keys are read through here rather than off the cookie getters.
  getCmsKeys(): Observable<any> {
    if (!this.cmsKeys$) {
      this.cmsKeys$ = this.http.get<any>(`${environment.apiURL}catalogs/message/items/app-config-params.gzip?auth_token=${this.cmsToken()}&region=IN&item_language=en`)
        .pipe(
          map((res) => this.extractCmsKeys(res)),
          tap((cmsKeys) => this.cacheCmsKeys(cmsKeys)),
          // don't let a failed fetch stay cached as a permanent error
          catchError((err) => { this.cmsKeys$ = null; return throwError(err); }),
          shareReplay(1)
        );
    }
    return this.cmsKeys$;
  }

  // transcoder host + token, resolved from the API with the cookies as fallback
  getTxConfig(): Observable<{ baseUrl: string, token: string }> {
    return this.getCmsKeys().pipe(
      map((cmsKeys) => ({
        baseUrl: this.withScheme((cmsKeys && cmsKeys.tx_base_url) || this.txBaseUrl),
        token: (cmsKeys && cmsKeys.user_auth_token) || this.userAuthToken
      }))
    );
  }

  get subscriptionId(): string { return this.cookieService.get('subscription_id'); }
  get accessControlId(): string { return this.cookieService.get('access_control_id'); }
  get couponsId(): string { return this.cookieService.get('coupons_id'); }
  get moviesCatalogId(): string { return this.cookieService.get('movies_catalog_id'); }
  get videosCatalogId(): string { return this.cookieService.get('videos_catalog_id'); }
  get episodesShowsCatalogId(): string { return this.cookieService.get('episodes_shows_catalog_id'); }
  get tvodPacksCatalogId(): string { return this.cookieService.get('tvod_packs_catalog_id'); }

  // transcoder host + its own auth token; both come from cms_keys, never hardcoded
  get txBaseUrl(): string { return this.cookieService.get('tx_base_url'); }
  get userAuthToken(): string { return this.cookieService.get('user_auth_token'); }

  // cms_keys stores the host without a scheme (e.g. '101.53.157.173:9900')
  private withScheme(host: string): string {
    if (!host) { return ''; }
    return /^https?:\/\//i.test(host) ? host.replace(/\/+$/, '') : `http://${host.replace(/\/+$/, '')}`;
  }

  get txBaseUrlWithScheme(): string {
    return this.withScheme(this.txBaseUrl);
  }

  getAppConfig(): Observable<any> {
    return this.http.get<any>(environment.apiURL+"app_config/update_app_config?auth_token="+environment.authtoken+"&active_session="+this.active_session)
    .pipe(
      retry(1),
      catchError(this.handleError)
     );
  }

  addAppConfigFields(keys: any, values: any){
    var addAppConfigFields = {};
    addAppConfigFields["keys"] = keys;
    addAppConfigFields["values"] = values;
    addAppConfigFields["id"] = "add_app_config"
    // addAppConfigFields["active_session"] = this.active_session;

    //console.log("the keys and values are",keys, values)
    return this.http.post<any>(environment.apiURL+"app_config/create?auth_token="+environment.authtoken+"&active_session="+this.active_session, JSON.stringify(addAppConfigFields), this.options)
    .pipe(
      retry(1),
      catchError(this.handleError)
     );
  }

  deleteAppConfigFields(keys: any){
    var addAppConfigFields = {};
    addAppConfigFields["keys"] = keys;
    addAppConfigFields["id"] = "delete_app_config"
    // addAppConfigFields["active_session"] = this.active_session;

    //console.log("the keys and values are",keys,addAppConfigFields)
    return this.http.post<any>(environment.apiURL+"app_config/delete_fields?auth_token="+environment.authtoken+"&active_session="+this.active_session, JSON.stringify(addAppConfigFields), this.options)
    .pipe(
      retry(1),
      catchError(this.handleError)
     );
  }

  updateAppConfig(appConFields: any){
    return this.http.put<any>(environment.apiURL+"app_config/update_app_config?auth_token="+environment.authtoken+"&active_session="+this.active_session+"&active_session="+this.active_session, JSON.stringify(appConFields), this.options)
    .pipe(
      retry(1),
      catchError(this.handleError)
     );
  }

  singleAppConfigFieldUpdate(appFields: any){
    
    return this.http.put<any>(environment.apiURL+"app_config/update_app_config?auth_token="+environment.authtoken+"&active_session="+this.active_session, JSON.stringify(appFields), this.options)
    .pipe(
      retry(1),
      catchError(this.handleError)
     );
  }


  // error handler
  handleError(error: HttpErrorResponse) {
    let errorMessage = '';
    //console.log(error);

    if (error.error instanceof ErrorEvent) {
       //console.log('client-side error')
      errorMessage = `Error: ${error.error.message}`;
    } else {
      //console.log('server-side error');
      //console.log(error);
      if(error.error.error.error_code){
        this.error_message = {
          server_error_code: error.error.error.error_code,
          server_error_messsage:error.error.error.error_message
        }
      }
      else if(error.error.error) {
        //console.log("error.error"+error.error)
        this.error_message = {
          server_error_code: error.error.error.code,
          server_error_messsage: error.error.error.message ? error.error.error.message : error.error.error
        }
      }
      else {
        //console.log("no:");
        this.error_message = {
          server_error_code: error.status,
          server_error_messsage: error.message
        }
      }

      //errorMessage = `server_error message: ${error.error.error.message}+Error Code: ${error.status}+Message: ${error.message}`;
      errorMessage = this.error_message;
    }
    return throwError(errorMessage);
  }
}
